<template>
  <section class="google-sheets-config">
    <h4 class="google-sheets-config__title">Configuración de Google Sheets de Envíos</h4>
    <p class="google-sheets-config__hint">
      Se sincronizará automáticamente el detalle de preliquidaciones del día (00:00 hora de Chile)
      al archivo de Google Sheets configurado.
    </p>

    <div v-if="!credentialsConfigured" class="google-sheets-config__alert google-sheets-config__alert--warning">
      <strong>Credenciales no configuradas.</strong>
      Las credenciales de la cuenta de servicio deben estar definidas en el servidor
      (<code>GOOGLE_SHEETS_CLIENT_EMAIL</code> y <code>GOOGLE_SHEETS_PRIVATE_KEY</code>).
      Comparte el archivo de Google Sheets con el email de la cuenta de servicio.
    </div>

    <div class="google-sheets-config__share">
      <h5 class="google-sheets-config__share-title">Compartir el archivo con la cuenta de servicio</h5>
      <p class="google-sheets-config__share-hint">
        Para que el sistema pueda escribir en tu archivo de Google Sheets, debes compartirlo
        con la siguiente cuenta de servicio:
      </p>

      <div class="google-sheets-config__share-email">
        <code>{{ serviceAccountEmail || '—' }}</code>
        <AppButton
          v-if="serviceAccountEmail"
          variant="secondary"
          outline
          type="button"
          @click="copyEmail"
        >
          Copiar
        </AppButton>
      </div>

      <ol class="google-sheets-config__steps">
        <li>Abre tu archivo de Google Sheets.</li>
        <li>Haz clic en el botón <strong>Compartir</strong> (arriba a la derecha).</li>
        <li>Pega el email de la cuenta de servicio en el campo de acceso.</li>
        <li>Asigna el permiso de <strong>Editor</strong>.</li>
        <li>Haz clic en <strong>Enviar</strong>.</li>
      </ol>
    </div>

    <form class="google-sheets-config__form" @submit.stop.prevent="onSubmit">
      <AppField
        label="Archivo de Google Sheets"
        description="ID del archivo o su URL completa. Ej: https://docs.google.com/spreadsheets/d/1AbC.../edit"
      >
        <AppInput
          v-model="form.spreadsheetId"
          maxlength="500"
          placeholder="ID o URL del Google Sheet"
        />
      </AppField>

      <AppField
        label="Nombre de la hoja"
        description="Pestaña del archivo donde se escribirán los datos."
      >
        <AppInput
          v-model="form.sheetName"
          maxlength="200"
          placeholder="Sheet1"
        />
      </AppField>

      <AppField label="Habilitado">
        <AppCheckbox v-model="form.enabled">
          Sincronizar automáticamente cada día
        </AppCheckbox>
      </AppField>

      <div class="google-sheets-config__actions">
        <AppButton
          variant="primary"
          type="submit"
          :disabled="loading || !credentialsConfigured"
        >
          Guardar
        </AppButton>
        <AppButton
          variant="secondary"
          outline
          type="button"
          :disabled="testing || !form.spreadsheetId || !credentialsConfigured"
          @click="onTest"
        >
          Probar conexión
        </AppButton>
        <AppButton
          v-if="debugEnabled"
          variant="danger"
          outline
          type="button"
          :disabled="syncing || !credentialsConfigured"
          @click="onSync"
        >
          {{ syncing ? 'Sincronizando...' : 'Forzar sincronización' }}
        </AppButton>
      </div>
      <p v-if="debugEnabled" class="google-sheets-config__debug-hint">
        Modo debug: este botón fuerza la sincronización del día anterior sin esperar a las 00:00.
      </p>
    </form>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { isDebugEnabled } from '../../utils/debugUtils'
import AppField from '../UI/AppField'
import AppInput from '../UI/AppInput'
import AppButton from '../UI/AppButton'
import AppCheckbox from '../UI/AppCheckbox'

export default {
  name: 'GoogleSheetsConfig',
  components: {
    AppField,
    AppInput,
    AppButton,
    AppCheckbox
  },
  data () {
    return {
      loading: false,
      testing: false,
      syncing: false,
      debugEnabled: false,
      credentialsConfigured: false,
      serviceAccountEmail: '',
      form: {
        spreadsheetId: '',
        sheetName: 'Sheet1',
        enabled: false
      }
    }
  },
  methods: {
    ...mapActions([
      'getSettlementGoogleSheetsConfig',
      'updateSettlementGoogleSheetsConfig',
      'testSettlementGoogleSheetsConnection',
      'syncSettlementGoogleSheets'
    ]),
    async fetchConfig () {
      try {
        const response = await this.getSettlementGoogleSheetsConfig()
        const config = response.data
        this.credentialsConfigured = !!config.credentialsConfigured
        this.serviceAccountEmail = config.serviceAccountEmail || ''
        this.form.spreadsheetId = config.spreadsheetId || ''
        this.form.sheetName = config.sheetName || 'Sheet1'
        this.form.enabled = !!config.enabled
      } catch (error) {
        this.$toast.danger('Error al cargar la configuración')
      }
    },
    async onSubmit () {
      this.loading = true
      try {
        await this.updateSettlementGoogleSheetsConfig({ data: this.form })
        this.$toast.success('Configuración guardada exitosamente')
      } catch (error) {
        this.$toast.danger(this.getErrorMessage(error))
      } finally {
        this.loading = false
      }
    },
    async onTest () {
      this.testing = true
      try {
        const response = await this.testSettlementGoogleSheetsConnection({ data: this.form })
        this.$toast.success(response.data.message || 'Conexión exitosa')
      } catch (error) {
        this.$toast.danger(this.getErrorMessage(error))
      } finally {
        this.testing = false
      }
    },
    async onSync () {
      this.syncing = true
      try {
        const response = await this.syncSettlementGoogleSheets({ data: {} })
        const statusMessages = {
          synced: `Sincronización exitosa: ${response.data.rowCount} filas escritas`,
          already_logged: 'La fecha ya estaba sincronizada en la hoja',
          disabled: 'La sincronización automática está deshabilitada',
          not_configured: 'El archivo de Google Sheets no está configurado',
          no_data: 'No hay envíos para la fecha'
        }
        const message = statusMessages[response.data.status] || 'Sincronización forzada'
        this.$toast.success(message)
      } catch (error) {
        this.$toast.danger(this.getErrorMessage(error))
      } finally {
        this.syncing = false
      }
    },
    getErrorMessage (error) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message
      }
      if (error && error.message) {
        return error.message
      }
      return 'Ocurrió un error'
    },
    async copyEmail () {
      try {
        await navigator.clipboard.writeText(this.serviceAccountEmail)
        this.$toast.success('Email copiado al portapapeles')
      } catch {
        this.$toast.danger('No se pudo copiar el email')
      }
    }
  },
  mounted () {
    this.debugEnabled = isDebugEnabled()
    this.fetchConfig()
  }
}
</script>

<style scoped>
.google-sheets-config {
  display: grid;
  gap: 1rem;
}

.google-sheets-config__title {
  margin: 0;
}

.google-sheets-config__hint {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.google-sheets-config__alert {
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.google-sheets-config__alert--warning {
  background-color: #fff3cd;
  border: 1px solid #ffe69c;
  color: #664d03;
}

.google-sheets-config__alert code {
  background-color: rgba(0, 0, 0, 0.08);
  padding: 0.1rem 0.3rem;
  border-radius: 0.2rem;
  font-size: 0.8em;
}

.google-sheets-config__share {
  display: grid;
  gap: 0.5rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: #f9fafb;
}

.google-sheets-config__share-title {
  margin: 0;
  font-size: 0.95rem;
}

.google-sheets-config__share-hint {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.google-sheets-config__share-email {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.google-sheets-config__share-email code {
  background-color: #fff;
  border: 1px solid #d1d5db;
  padding: 0.4rem 0.6rem;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  word-break: break-all;
  flex: 1;
  min-width: 0;
}

.google-sheets-config__steps {
  margin: 0;
  padding-left: 1.25rem;
  color: #374151;
  font-size: 0.875rem;
  display: grid;
  gap: 0.25rem;
}

.google-sheets-config__form {
  display: grid;
  gap: 0.5rem;
}

.google-sheets-config__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.google-sheets-config__debug-hint {
  margin: 0;
  color: #9a3412;
  font-size: 0.8rem;
}
</style>
